import { useState, useEffect, useCallback } from "react";
import { Plus, CalendarDays, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getUserGymId } from "@/utils/gymAuth";
import { toast } from "sonner";
import type { GymCalendar } from "@/types/types";
import { listCalendars, deleteCalendar } from "@/services/edgeFunctionWrappers/calendarsWrapper";
import { CalendarFormDialog } from "@/components/calendars/CalendarFormDialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function CalendarsPage() {
  const { t } = useTranslation(["adminui3", "common"]);
  const gymId = getUserGymId();
  const [calendars, setCalendars] = useState<GymCalendar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GymCalendar | null>(null);
  const [deleting, setDeleting] = useState<GymCalendar | null>(null);

  // Création de calendrier interrompue par un reload → proposer de la reprendre.
  useDraftResumeToast([
    {
      formId: "calendar-form:new",
      message: t("calendars.draftUnsaved"),
      description: (d) => (d.name as string) || undefined,
      isEmpty: (d) => !d.name,
      onResume: () => {
        setEditing(null);
        setFormOpen(true);
      },
    },
  ]);

  const fetchCalendars = useCallback(async () => {
    if (!gymId) return;
    setIsLoading(true);
    try {
      const data = await listCalendars(gymId, showInactive);
      setCalendars(data);
    } catch (e) {
      console.error(e);
      toast.error(t("calendars.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [gymId, showInactive]);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (cal: GymCalendar) => {
    setEditing(cal);
    setFormOpen(true);
  };

  const handleDeleteRequest = (cal: GymCalendar) => {
    setDeleting(cal);
  };

  const handleDeleteConfirm = async () => {
    if (!gymId || !deleting) return;
    try {
      await deleteCalendar(gymId, deleting.id);
      toast.success(t("calendars.deleteSuccess"));
      setDeleting(null);
      fetchCalendars();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("calendars.deleteError");
      toast.error(message);
    }
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setEditing(null);
    fetchCalendars();
  };

  return (
    <div className="container mx-auto py-4 md:py-6 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 md:h-8 md:w-8 text-primary" />
          <div>
            <h1 className="text-lg md:text-2xl font-bold">{t("calendars.title")}</h1>
            <p className="text-muted-foreground text-xs md:text-sm hidden sm:block">
              {t("calendars.subtitle")}
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t("calendars.add")}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-2">
              <Switch
                id="show-inactive-calendars"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive-calendars" className="text-xs md:text-sm text-muted-foreground">
                {t("calendars.inactive")}
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground py-4">{t("calendars.loading")}</p>
          ) : calendars.length === 0 ? (
            <p className="text-muted-foreground py-4">{t("calendars.empty")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs md:text-sm">{t("calendars.columnColor")}</TableHead>
                  <TableHead className="text-xs md:text-sm">{t("calendars.columnName")}</TableHead>
                  <TableHead className="text-xs md:text-sm hidden md:table-cell">{t("calendars.columnDescription")}</TableHead>
                  <TableHead className="text-xs md:text-sm text-right hidden md:table-cell">{t("calendars.columnClasses")}</TableHead>
                  <TableHead className="text-xs md:text-sm">{t("calendars.columnStatus")}</TableHead>
                  <TableHead className="text-xs md:text-sm w-[80px]">{t("calendars.columnActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calendars.map((cal) => (
                  <TableRow key={cal.id}>
                    <TableCell>
                      <span
                        className="inline-block w-4 h-4 md:w-5 md:h-5 rounded border"
                        style={{ backgroundColor: cal.color ?? "#94a3b8" }}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-xs md:text-sm">{cal.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate hidden md:table-cell text-xs md:text-sm">
                      {cal.description ?? "—"}
                    </TableCell>
                    <TableCell className="text-right hidden md:table-cell text-xs md:text-sm">{cal.classes_count ?? 0}</TableCell>
                    <TableCell>
                      {cal.is_active ? (
                        <span className="text-green-600">{t("calendars.active")}</span>
                      ) : (
                        <span className="text-muted-foreground">{t("calendars.inactiveStatus")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(cal)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRequest(cal)}
                          disabled={(cal.classes_count ?? 0) > 0}
                          title={(cal.classes_count ?? 0) > 0 ? t("calendars.deleteBlockedTitle") : t("calendars.delete")}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CalendarFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editingCalendar={editing}
        onSuccess={handleFormSuccess}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendars.deleteDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendars.deleteDialogDescription", { name: deleting?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("calendars.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
              {t("calendars.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
